import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';

/* Preloader — PRASTARA logo on black, fades out */
export function Preloader() {
  const [show, setShow] = useState(true);
  useEffect(() => { const t = setTimeout(() => setShow(false), 2000); return () => clearTimeout(t); }, []);
  if (!show) return null;
  return (
    <div className="preloader" data-testid="preloader">
      <img src="/logo-light.png" alt="PRASTARA" className="h-14 w-auto" />
    </div>
  );
}

/* Custom cursor — dot that grows on hover */
export function CustomCursor() {
  const ref = useRef(null);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    const move = (e) => { if (ref.current) { ref.current.style.left = e.clientX + 'px'; ref.current.style.top = e.clientY + 'px'; } };
    const over = (e) => { if (e.target.closest('a, button, [role="button"], input, textarea, select')) setHovering(true); else setHovering(false); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseover', over);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseover', over); };
  }, []);

  return <div ref={ref} className={`custom-cursor ${hovering ? 'hovering' : ''}`} />;
}

/* Back to top button */
export function BackToTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const h = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <button className={`back-to-top ${visible ? 'visible' : ''}`} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} data-testid="back-to-top" aria-label="Back to top">
      <ArrowUp className="w-5 h-5" />
    </button>
  );
}

/* Cookie consent banner */
export function CookieBanner() {
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  const isAdminRoute = pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdminRoute) return undefined;
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) { const t = setTimeout(() => setShow(true), 2500); return () => clearTimeout(t); }
    return undefined;
  }, [isAdminRoute]);

  const accept = () => { localStorage.setItem('cookie_consent', 'accepted'); setShow(false); };

  if (isAdminRoute) return null;

  return (
    <aside className={`cookie-banner ${show ? 'visible' : ''}`} aria-label="Cookie notice" data-testid="cookie-banner">
      <div className="px-5 py-4">
        <p className="pr-eyebrow mb-2 text-stone">Privacy notice</p>
        <p className="text-[13px] leading-relaxed text-white/65 mb-4" style={{ fontFamily: 'var(--font-body)', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal' }}>
          We use cookies to improve your experience. By continuing, you agree to our privacy policy.
        </p>
        <div className="flex gap-2">
          <button onClick={accept} className="border border-arch-white bg-arch-white px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-soft-black transition-colors duration-150 hover:bg-warm-ivory" data-testid="cookie-accept-btn" aria-label="Accept cookies">Accept</button>
          <button onClick={() => setShow(false)} className="border border-white/30 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-white/10" data-testid="cookie-close-btn" aria-label="Close cookie notice">Close</button>
        </div>
      </div>
    </aside>
  );
}

/* Page transition wrapper */
export function PageTransition({ children }) {
  const { pathname } = useLocation();
  return <div key={pathname} className="page-transition">{children}</div>;
}

/* Animated counter */
export function AnimatedCounter({ value, suffix = '' }) {
  const ref = useRef(null);
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started) {
        setStarted(true);
        const target = parseInt(value.replace(/[^0-9]/g, ''));
        if (isNaN(target)) { setCount(value); return; }
        let c = 0;
        const step = target / 30;
        const timer = setInterval(() => {
          c += step;
          if (c >= target) { setCount(target); clearInterval(timer); }
          else setCount(Math.floor(c));
        }, 40);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value, started]);

  const display = typeof count === 'number' ? count.toLocaleString() : count;
  return <span ref={ref}>{display}{suffix}</span>;
}
